import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../../types';
import { authAPI } from '../../services/api';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<User>;
  register: (username: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {
    throw new Error('useAuth must be used within AuthProvider');
  },
  register: async () => false,
  logout: () => {},
  loading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authAPI.getCurrentUser()
        .then(userData => {
          // Map is_admin to role with explicit type casting
          const userWithRole: User = {
            ...userData,
            role: userData.is_admin ? 'admin' as const : 'user' as const
          };
          setUser(userWithRole);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username: string, password: string): Promise<User> => {
    try {
      const response = await authAPI.login(username, password);
      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        const userData = await authAPI.getCurrentUser();
        // Map is_admin to role with explicit type casting
        const userWithRole: User = {
          ...userData,
          role: userData.is_admin ? 'admin' as const : 'user' as const
        };
        setUser(userWithRole);
        return userWithRole;
      }
      throw new Error('Login failed');
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (username: string, email: string, password: string): Promise<boolean> => {
    try {
      await authAPI.register(username, email, password);
      const userData = await login(username, password);
      return !!userData;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
